import React, { useContext, useMemo } from 'react';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import flatten from 'lodash/flatten';
import maxBy from 'lodash/maxBy';

import RouteWrapper from './RouteWrapper';
import { DataContext } from '../components/data/DataContext';
import { DataContextType } from '../components/data_types/interfaces';
import {
    MIN_PERIOD_ROUNDS,
    PeriodSummary,
    TIME_BLOCKS,
    WEEKDAYS,
    bestPeriod,
    busiestPeriod,
    summarizeByHour,
    summarizeByTimeBlock,
    summarizeWeekdayByTimeBlock,
    toughestPeriod,
} from '../components/data/period_insights';
import { AvgChangeBarChart, ChartLegendNote, InsightCard, PeriodNav, PeriodTile } from '../components/shared/PeriodComponents';
import { DS, Overline, Panel, formatSigned, numericSx, trendColor } from '../components/shared/Dashboard';

const WeekTimeGrid: React.FC<{ grid: PeriodSummary[][]; best?: PeriodSummary }> = ({ grid, best }) => {
    const maxAbs = Math.max(...flatten(grid).filter(c => c.reliable).map(c => Math.abs(c.avgChange)), 1);

    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '48px repeat(4, minmax(64px, 1fr))', gap: 0.75, minWidth: 320 }}>
                <Box />
                {TIME_BLOCKS.map(b => (
                    <Box key={b.key} sx={{ textAlign: 'center', pb: 0.5 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: DS.text }}>{b.label}</Typography>
                        <Typography sx={{ fontSize: '11px', color: DS.textFaint, ...numericSx }}>{b.range}</Typography>
                    </Box>
                ))}
                {grid.map((row, r) => (
                    <React.Fragment key={WEEKDAYS[r].day}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: DS.textMuted, display: 'flex', alignItems: 'center' }}>
                            {WEEKDAYS[r].short}
                        </Typography>
                        {row.map(cell => {
                            const color = trendColor(cell.avgChange);
                            const strength = cell.reliable ? 0.18 + 0.55 * Math.min(Math.abs(cell.avgChange) / maxAbs, 1) : 0.08;
                            const isBest = best?.key === cell.key;
                            return (
                                <Tooltip
                                    key={cell.key}
                                    arrow
                                    title={cell.count
                                        ? `${cell.label}: ${formatSigned(cell.avgChange, 1)} Elo per round over ${cell.count} rounds${cell.reliable ? '' : ' (small sample)'}`
                                        : `${cell.label}: no rounds`}
                                >
                                    <Box sx={{
                                        height: 44,
                                        borderRadius: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: cell.count ? alpha(cell.avgChange === 0 ? DS.neutral : color, strength) : DS.inset,
                                        border: isBest ? `2px solid ${DS.gain}` : `1px solid ${DS.border}`,
                                    }}>
                                        <Typography sx={{
                                            fontSize: '13px',
                                            fontWeight: 700,
                                            lineHeight: 1.1,
                                            color: cell.count ? (cell.reliable ? DS.text : DS.textMuted) : DS.textFaint,
                                            ...numericSx,
                                        }}>
                                            {cell.count ? formatSigned(cell.avgChange, 1) : '—'}
                                        </Typography>
                                        {cell.count > 0 && (
                                            <Typography sx={{ fontSize: '10px', color: DS.textMuted, lineHeight: 1.1, ...numericSx }}>
                                                {cell.count} rd{cell.count !== 1 ? 's' : ''}
                                            </Typography>
                                        )}
                                    </Box>
                                </Tooltip>
                            );
                        })}
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    );
};

const ByHour = () => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { historyData } = context || {};

    const history = historyData ?? [];
    const blocks = useMemo(() => summarizeByTimeBlock(history), [history]);
    const hours = useMemo(() => summarizeByHour(history), [history]);
    const grid = useMemo(() => summarizeWeekdayByTimeBlock(history), [history]);

    const best = bestPeriod(blocks);
    const toughest = toughestPeriod(blocks);
    const busiestHour = busiestPeriod(hours);
    const bestSlot = bestPeriod(flatten(grid));
    const busiestBlock = maxBy(blocks, 'count');
    const needMore = `Play ${MIN_PERIOD_ROUNDS}+ rounds in a time slot to rank it.`;

    return (
        <RouteWrapper>
            <PeriodNav title="Your clock" subtitle="When in the day you play your best. Times are in your local time zone." />

            <InsightCard overline="Best time of day" accent={DS.gain} period={best} metric="avg" emptyText={needMore} delay={0.05} />
            <InsightCard overline="Toughest time of day" accent={DS.loss} period={best && toughest?.key !== best.key ? toughest : undefined} metric="avg" emptyText={needMore} delay={0.1} />
            <InsightCard
                overline="Most played hour"
                accent={DS.info}
                period={busiestHour && { ...busiestHour, label: `${busiestHour.label}–${String(Number(busiestHour.key) + 1).padStart(2, '0')}:00` }}
                metric="count"
                emptyText="No rounds yet."
                delay={0.15}
            />

            <Panel
                title="Best time to play"
                subtitle={bestSlot
                    ? <>Your sweet spot is <strong style={{ color: DS.gain }}>{bestSlot.label}</strong>, averaging {formatSigned(bestSlot.avgChange, 1)} Elo per round.</>
                    : `Average Elo per round for each day and time. Slots need ${MIN_PERIOD_ROUNDS}+ rounds to be ranked.`}
                grid={{ xs: 12, md: 7 }}
                delay={0.1}
            >
                <WeekTimeGrid grid={grid} best={bestSlot} />
            </Panel>

            <Panel title="Time of day" subtitle={busiestBlock?.count ? `You play most in the ${busiestBlock.label.toLowerCase()}` : undefined} grid={{ xs: 12, md: 5 }} delay={0.15}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
                    {blocks.map((block, i) => (
                        <PeriodTile
                            key={block.key}
                            period={{ ...block, label: `${block.label} ${TIME_BLOCKS[i].range}` }}
                            delay={0.15 + i * 0.03}
                            highlight={block.key === best?.key ? 'best' : block.key === toughest?.key && toughest?.key !== best?.key ? 'toughest' : undefined}
                        />
                    ))}
                </Box>
            </Panel>

            <Panel title="Hour by hour" subtitle="Average rating change per round, by the hour you started playing" delay={0.2}>
                <ChartLegendNote />
                <AvgChangeBarChart periods={hours} labels={hours.map((_, h) => String(h).padStart(2, '0'))} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                    <Overline>Rounds per hour</Overline>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(24, minmax(0, 1fr))', gap: '3px', alignItems: 'end', height: 36, mt: 0.5 }}>
                    {hours.map(h => {
                        const max = Math.max(...hours.map(x => x.count), 1);
                        return (
                            <Tooltip key={h.key} arrow title={`${h.label}: ${h.count} rounds`}>
                                <Box sx={{ height: `${Math.max((h.count / max) * 100, h.count ? 8 : 3)}%`, borderRadius: '2px', background: h.count ? alpha(DS.info, 0.6) : DS.inset }} />
                            </Tooltip>
                        );
                    })}
                </Box>
            </Panel>

            <Grid item xs={12} sx={{ mb: 6 }} />
        </RouteWrapper>
    );
};

export default ByHour;
